// @givmo/board — the officer's statement: generated FOR the officer, executed BY the officer.
//
// ══ WHAT THIS IS, AND THE TWO THINGS IT IS CAREFUL NOT TO BE ════════════════════════════════════
//
// Plan §3.7: "the module is the book, never the officer", and "the product certifies nothing". So what
// the backend renders here is a SCAFFOLD — the record's own facts filled in, and the officer's own
// terms left as `[bracketed]` fields for them to complete and sign outside this product. Two
// substitution mechanisms, and the split IS the rule made structural: braced placeholders carry facts
// the organization already recorded, bracketed fields are the officer's. There is no placeholder in which
// the product could state anything of its own, which is why this panel can render the text verbatim
// without reading it.
//
// The two things this surface must never do, both of which a well-meaning UI does by default:
//
//   1. It must not describe the statement as anything the PRODUCT has done to the record. §3.4 bans
//      that word's root down to field names — which is why the backend named the artifact and its route
//      as it did, and why nothing on this panel names it any other way either. The terminology rail
//      refuses the other spelling.
//   2. It must not present the text as finished. It is not: it has the officer's own fields still in
//      brackets, and it becomes a statement when a human signs it. The copy says so above the text,
//      before the copy and download buttons rather than after them.
//
// Copy and download both hand over the text EXACTLY as the backend rendered it, brackets included. A
// helpful client-side pass that stripped the brackets, or filled one in, would be the product writing a
// term into an officer's own statement.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type BoardOfficerStatementOut } from '@givmo/api-client';
import { Button, Card, CardBody, ErrorMessage, Skeleton, toast } from '@givmo/design-system';
import { TemplateNotice } from './template-notice';
import { useBoardOperations } from '../../board-runtime';

/** A filename a human will recognize in their downloads folder. */
export function officerStatementFileName(consentTitle: string): string {
  const slug = consentTitle
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug.length > 0 ? slug : 'board-record'}-officer-statement.txt`;
}

/** Hand the statement text to the browser as a download, byte for byte as it was rendered. */
export function downloadOfficerStatement(text: string, fileName: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export interface OfficerStatementPanelProps {
  orgRef: string;
  /** The FILED record's id — a consent that has not completed has no record and no statement. */
  recordId: string;
  consentTitle: string;
}

export function OfficerStatementPanel({
  orgRef,
  recordId,
  consentTitle,
}: OfficerStatementPanelProps) {
  const operations = useBoardOperations();
  const [open, setOpen] = useState(false);

  const statementQuery = useQuery({
    queryKey: ['board-officer-statement', orgRef, recordId],
    enabled: open,
    queryFn: async (): Promise<BoardOfficerStatementOut> => {
      const { data } = await operations.officerGetBoardOfficerStatement({
        path: { org_ref: orgRef, record_id: recordId },
        throwOnError: true,
      });
      return data;
    },
  });

  const statement = statementQuery.data ?? null;

  return (
    <Card testId="consent-officer-statement">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-4">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              A statement for an officer to sign
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Banks, registries and counterparties often ask an officer of the organization to state
              what the board did. This puts this record’s own details into a draft for your officer
              to complete and sign — every statement in it is theirs, made outside Givmo.
            </p>
          </div>
          {!open ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setOpen(true)}
              testId="consent-officer-statement-open"
            >
              Draw one up
            </Button>
          ) : null}
        </div>

        {open && statementQuery.isPending ? (
          <Skeleton className="h-90 w-full rounded-lg" testId="consent-officer-statement-loading" />
        ) : null}

        {open && statementQuery.isError ? (
          <ErrorMessage testId="consent-officer-statement-error">
            We couldn’t draw that up just now. Please try again.
          </ErrorMessage>
        ) : null}

        {statement !== null ? (
          <>
            <TemplateNotice
              orgRef={orgRef}
              reviewLine={statement.review_line}
              provenance={statement.provenance}
              sourceCitations={statement.source_citations}
              testId="consent-officer-statement-notice"
            />

            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="consent-officer-statement-brackets"
            >
              Anything still in square brackets is for your officer to complete in their own words
              before they sign it.
            </p>

            <pre
              className="max-h-[420px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="consent-officer-statement-text"
            >
              {statement.statement_text}
            </pre>

            <div className="flex flex-wrap gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(statement.statement_text)
                    .then(() => toast.success('Copied.'))
                    .catch(() => toast.error('We couldn’t copy it. Select the text instead.'));
                }}
                testId="consent-officer-statement-copy"
              >
                Copy the text
              </Button>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() =>
                  downloadOfficerStatement(
                    statement.statement_text,
                    officerStatementFileName(consentTitle),
                  )
                }
                testId="consent-officer-statement-download"
              >
                Download it
              </Button>
            </div>
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
